/**
 * useFipeLookup - Consultas à tabela FIPE via Edge Function `fipe-proxy`
 *
 * Arquitetura multi-provider: o frontend nunca conhece o provedor; a Edge
 * Function roteia e mantém o cache global (fipe_brands/models/years/price).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-08 v1.1.0 - Novo `useFipeResolve()` (mutation) para resolver
 *          uma correspondência FIPE escolhida pelo usuário no modal de opções.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { VehicleType } from '@/hooks/useCustomerVehicles';

const logger = createLogger('useFipeLookup');


/** Tipos FIPE (exclui OTHER, que é categoria do tenant) */
export type FipeVehicleType = Exclude<VehicleType, 'OTHER'>;

/** Item genérico de marca/modelo */
export interface FipeOption {
  id: string;
  codigo_marca?: string;
  codigo_modelo?: string;
  nome_marca?: string;
  nome_modelo?: string;
}

/** Ano/combustível de um modelo */
export interface FipeYearOption {
  id: string;
  codigo_ano: string;
  ano: number | null;
  combustivel: string | null;
  label: string;
}

/** Preço FIPE consultado */
export interface FipePrice {
  fipe_code: string | null;
  valor: number | null;
  mes_referencia: string | null;
}

/** Chamada base à Edge Function */
async function callFipe<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('fipe-proxy', { body });
  if (error) {
    logger.error('Falha na consulta FIPE', { error: error.message, body });
    throw new Error('Não foi possível consultar a tabela FIPE');
  }
  return data as T;
}

/** Marcas de um tipo de veículo */
export function useFipeBrands(vehicleType: FipeVehicleType | null) {
  return useQuery({
    queryKey: ['fipe-brands', vehicleType],
    enabled: !!vehicleType,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const data = await callFipe<{ items: FipeOption[] }>({
        action: 'brands',
        vehicle_type: vehicleType,
      });
      return data.items || [];
    },
  });
}

/** Modelos de uma marca */
export function useFipeModels(vehicleType: FipeVehicleType | null, brandId: string | null) {
  return useQuery({
    queryKey: ['fipe-models', vehicleType, brandId],
    enabled: !!vehicleType && !!brandId,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const data = await callFipe<{ items: FipeOption[] }>({
        action: 'models',
        vehicle_type: vehicleType,
        brand_id: brandId,
      });
      return data.items || [];
    },
  });
}

/** Anos/combustíveis de um modelo */
export function useFipeYears(vehicleType: FipeVehicleType | null, modelId: string | null) {
  return useQuery({
    queryKey: ['fipe-years', vehicleType, modelId],
    enabled: !!vehicleType && !!modelId,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const data = await callFipe<{ items: FipeYearOption[] }>({
        action: 'years',
        vehicle_type: vehicleType,
        model_id: modelId,
      });
      return data.items || [];
    },
  });
}

/** Preço/código FIPE de um ano específico */
export function useFipePrice(
  vehicleType: FipeVehicleType | null,
  modelId: string | null,
  yearCode: string | null,
) {
  return useQuery({
    queryKey: ['fipe-price', vehicleType, modelId, yearCode],
    enabled: !!vehicleType && !!modelId && !!yearCode,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () =>
      await callFipe<FipePrice>({
        action: 'price',
        vehicle_type: vehicleType,
        model_id: modelId,
        year_code: yearCode,
      }),
  });
}

/**
 * v1.1.0 — Resolve uma correspondência FIPE (texto do provedor de placa) nos
 * IDs da base global FIPE. Usado quando o usuário escolhe manualmente uma das
 * opções devolvidas pela consulta por placa.
 */
export interface FipeResolveInput {
  vehicle_type: FipeVehicleType;
  brand_text?: string | null;
  model_text?: string | null;
  year?: number | null;
  fuel?: string | null;
  fipe_code?: string | null;
}

export interface FipeResolveResult {
  matched: boolean;
  confidence?: 'confirmed' | 'suggested';
  reason?: string;
  brand_id?: string | null;
  brand_name?: string | null;
  model_id?: string | null;
  model_name?: string | null;
  year_code?: string | null;
  year_label?: string | null;
  fipe_code?: string | null;
  valor?: number | null;
  mes_referencia?: string | null;
}

export function useFipeResolve() {
  return useMutation({
    mutationFn: async (input: FipeResolveInput): Promise<FipeResolveResult> =>
      await callFipe<FipeResolveResult>({ action: 'resolve', ...input }),
  });
}
